import styled from 'styled-components';
import { theme } from '../../styles/theme';

export interface AlertProps {
  children: React.ReactNode;
  variant?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  onClose?: () => void;
}

const AlertWrapper = styled.div<{ variant: 'info' | 'success' | 'warning' | 'error' }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  gap: ${theme.spacing.md};
  align-items: flex-start;
  direction: rtl;
  text-align: right;
  position: relative;
  
  ${({ variant }) => {
    switch (variant) {
      case 'success':
        return `
          background-color: rgba(46, 204, 113, 0.1);
          border: 2px solid ${theme.colors.success};
          color: #27ae60;
        `;
      case 'warning':
        return `
          background-color: rgba(243, 156, 18, 0.1);
          border: 2px solid ${theme.colors.warning};
          color: #d68910;
        `;
      case 'error':
        return `
          background-color: rgba(231, 76, 60, 0.1);
          border: 2px solid ${theme.colors.error};
          color: #c0392b;
        `;
      default:
        return `
          background-color: rgba(78, 205, 196, 0.1);
          border: 2px solid ${theme.colors.secondary};
          color: ${theme.colors.secondaryDark};
        `;
    }
  }}
`;

const AlertIcon = styled.div`
  font-size: ${theme.fontSizes.xl};
  line-height: 1;
  flex-shrink: 0;
`;

const AlertContent = styled.div`
  flex: 1;
`;

const AlertTitle = styled.h4`
  margin: 0 0 ${theme.spacing.xs} 0;
  font-size: ${theme.fontSizes.md};
  font-weight: 600;
`;

const AlertMessage = styled.div`
  font-size: ${theme.fontSizes.sm};
  line-height: 1.5;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: ${theme.fontSizes.xl};
  line-height: 1;
  color: currentColor;
  opacity: 0.6;
  transition: opacity ${theme.transitions.fast};
  
  &:hover {
    opacity: 1;
  }
`;

const getIcon = (variant: 'info' | 'success' | 'warning' | 'error') => {
  switch (variant) {
    case 'success':
      return '✓';
    case 'warning':
      return '⚠';
    case 'error':
      return '✕';
    default:
      return 'ℹ';
  }
};

export const Alert: React.FC<AlertProps> = ({ 
  children, 
  variant = 'info', 
  title,
  onClose 
}) => {
  return (
    <AlertWrapper variant={variant}>
      <AlertIcon>{getIcon(variant)}</AlertIcon>
      <AlertContent>
        {title && <AlertTitle>{title}</AlertTitle>}
        <AlertMessage>{children}</AlertMessage>
      </AlertContent>
      {onClose && <CloseButton onClick={onClose}>×</CloseButton>}
    </AlertWrapper>
  );
};
